/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../platform/Platform';
import { ComponentManagerFn } from '../../../registry/Component.type';
import { baseComponentManager } from '../../../registry/Components';
import { defaultValue } from './LOVMap';

const LOVMapManager: ComponentManagerFn<'LOVMap'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'LOVMap'>(platform, 'LOVMap'),
    type: 'LOVMap',
    name: 'LOV Map',
    defaultValue,
    isPropertyVisible: (prop) => {
      switch (prop) {
        case 'sourceAttribute':
        case 'targetAttribute':
          return true;
        default:
          return false;
      }
    },
    props: {
      sourceAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Source Attribute',
        required: true,
        type: 'ViewAttributeChooser',
      },
      targetAttribute: {
        aliasType: 'STORE',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        label: 'Target Attribute',
        preventMultiEdit: true,
        type: 'ViewAttributeChooser',
      },
    },
    getDisplayText: (item) => item.props.targetAttribute,
    category: 'Forms',
    icon: { iconName: 'arrows-h' },
    description: `**Overview**<br/>
The LOV Map component is a data transfer tool designed to work exclusively within an **LOV Field**. It enables "autofill" functionality: when a user selects a record from the LOV popup, this component automatically copies specific values from that selected record into other fields on your form.
Use this component to populate related information instantly—for example, selecting a "Customer" in the main field can automatically fill in the "Shipping Address" and "Payment Terms" fields using LOV Maps.

**1. Mapping Logic**<br/>
The component functions as a bridge between two data contexts: the external list (LOV) and your current form (Store).
* **Source Attribute (sourceAttribute):** The field in the LOV's data source (the popup list) that contains the value you want to copy.
* **Alias Type:** **LOV** (Refers to the LOV's connected Data Store).
* **Target Attribute (targetAttribute):** The field in your current form's Data Store where the value should be pasted.
* **Alias Type:** **STORE** (Refers to the Form's Data Store).

**2. Configuration**<br/>
This component has no visual presence on the screen. It is purely a logic definition.
* **Visibility:** All standard layout properties are hidden. Only **sourceAttribute** and **targetAttribute** are configurable.
* **Multiple Maps:** You can add multiple LOV Map components inside a single LOV Field to update many fields simultaneously from a single user selection.

**3. Best Use Cases**<br/>
* **Auto-Complete Forms:** When a user selects a "Part Number," use LOV Maps to automatically fill the "Description," "Unit Price," and "Tax Code" fields.
* **Address Filling:** Selecting a "Zip Code" from a lookup list populates the "City" and "State" fields automatically.
* **Cross-Reference:** Selecting an "Employee" populates their "Manager's Name" and "Department ID" into fields for reporting purposes.`,
  };
};

export default LOVMapManager;
